import { mapApiMessageRow, type ChatMessage, type ChatMessageApiRow } from "@/lib/chat/map-api-message"

export type FetchChatMessagesResult = {
  messages: ChatMessage[]
  conversationId: string | null
}

export class ChatMessagesFetchError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = "ChatMessagesFetchError"
    this.status = status
  }
}

export async function fetchChatMessages(opts: {
  roomName: string
  limit: number
  before?: string
  /**
   * Optional cancellation, mirroring `fetchRefundState(id, { signal })`.
   * Added for `hooks/use-chat-last-message.ts`, whose effect must be able to
   * drop an in-flight response when the room changes — otherwise a slow reply
   * for order A lands on order B's card. Optional and additive: the three
   * existing callers (all via `fetchChatMessagesWithRetry`) pass no signal and
   * behave exactly as before. Extended, not forked (CLAUDE.md § Silent NACK).
   */
  signal?: AbortSignal
}): Promise<FetchChatMessagesResult> {
  const params = new URLSearchParams({
    roomName: opts.roomName,
    limit: String(opts.limit),
  })
  if (opts.before) {
    params.set("before", opts.before)
  }

  const res = await fetch(`/api/chat/messages?${params.toString()}`, {
    signal: opts.signal,
  })
  if (!res.ok) {
    let detail = res.statusText
    try {
      const payload = (await res.json()) as { error?: string }
      if (payload?.error) detail = payload.error
    } catch {}
    throw new ChatMessagesFetchError(res.status, detail || `Chat messages fetch failed (${res.status})`)
  }

  const json = (await res.json()) as {
    messages?: ChatMessageApiRow[]
    conversationId?: string
  }
  const rows = Array.isArray(json.messages) ? json.messages : []

  const messages = rows.flatMap((row) => {
    try {
      return [mapApiMessageRow(row)]
    } catch (error) {
      console.warn("[CHAT CLIENT] skipped malformed message row", { id: row?.id, error })
      return []
    }
  })

  return {
    messages,
    conversationId: typeof json.conversationId === "string" ? json.conversationId : null,
  }
}
